import { Menu, Search, Bell, HelpCircle, Moon, User, Users, Briefcase, X } from "lucide-react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { listEmployees } from "../api/employees";

export default function Topbar({ onMenuClick }) {
  const { partner } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch all employees on mount
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await listEmployees({ limit: 100 });
        setAllEmployees(res.data.items || []);
      } catch (error) {
      }
    };
    fetchEmployees();
  }, []);

  // Handle search input change
  useEffect(() => {
    if (searchQuery.trim().length === 0) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = allEmployees.filter(emp => 
      emp.name?.toLowerCase().includes(query) ||
      emp.userId?.toLowerCase().includes(query) ||
      emp.designation?.toLowerCase().includes(query) ||
      emp.phone?.includes(query)
    );

    setSuggestions(filtered.slice(0, 10));
    setShowSuggestions(true);
    setSelectedIndex(-1);
  }, [searchQuery, allEmployees]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showSuggestions || suggestions.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % suggestions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelectEmployee(suggestions[selectedIndex]);
        } else if (suggestions.length === 1) {
          handleSelectEmployee(suggestions[0]);
        } else if (searchQuery.trim().length > 0) {
          // Navigate to employees list with search query
          navigate(`/employees?search=${encodeURIComponent(searchQuery.trim())}`);
          setShowSuggestions(false);
          setSearchQuery("");
        }
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
        setSearchQuery("");
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showSuggestions, suggestions, selectedIndex, searchQuery, navigate]);

  const handleSelectEmployee = (employee) => {
    navigate(`/employees/${employee._id}`);
    setSearchQuery("");
    setShowSuggestions(false);
    setSuggestions([]);
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSuggestions([]);
    setShowSuggestions(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <header className="sticky top-0 z-20 flex items-center gap-4 border-b border-emerald-100/60 bg-white/80 px-5 py-3.5 backdrop-blur-nav lg:px-8 shadow-sm">
      <button
        onClick={onMenuClick}
        className="rounded-md p-2 text-gray-600 hover:bg-emerald-50 hover:text-emerald-700 lg:hidden transition-colors duration-200"
      >
        <Menu size={20} />
      </button>

      {/* Search Bar with Autocomplete */}
      <div className="relative flex max-w-md flex-1" ref={searchRef}>
        <div className="flex w-full items-center gap-2 rounded-full border border-emerald-200/70 bg-white px-4 py-2 focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-500/20 transition-all duration-200">
          <Search size={16} className="shrink-0 text-gray-400" />
          <input
            ref={inputRef}
            placeholder="Search employees by name, ID, or designation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => {
              if (searchQuery.trim().length > 0 && suggestions.length > 0) {
                setShowSuggestions(true);
              }
            }}
            className="w-full min-w-0 bg-transparent text-body-sm text-gray-800 placeholder:text-gray-400 focus:outline-none"
          />
          {searchQuery && (
            <button
              onClick={clearSearch}
              className="shrink-0 p-0.5 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-100"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Suggestions Dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute left-0 right-0 top-full mt-2 max-h-80 overflow-y-auto rounded-xl border border-emerald-200/70 bg-white shadow-2xl shadow-emerald-200/50 z-50">
            <div className="p-2 border-b border-emerald-100/60">
              <span className="text-xs font-medium text-gray-500">
                {suggestions.length} employee{suggestions.length > 1 ? 's' : ''} found
              </span>
            </div>
            {suggestions.map((employee, index) => (
              <div
                key={employee._id}
                onClick={() => handleSelectEmployee(employee)}
                className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors duration-150 ${
                  index === selectedIndex
                    ? 'bg-emerald-50 border-l-4 border-emerald-500'
                    : 'hover:bg-emerald-50/50'
                }`}
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-xs font-bold text-white shadow-md shadow-emerald-500/20">
                  {getInitials(employee.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900">{employee.name}</span>
                    <span className="text-xs text-gray-400">•</span>
                    <span className="text-xs text-gray-500">{employee.userId}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Briefcase size={11} className="text-gray-400" />
                      {employee.designation || 'No designation'}
                    </span>
                    {employee.phone && (
                      <>
                        <span className="text-xs text-gray-300">|</span>
                        <span className="text-xs text-gray-500">{employee.phone}</span>
                      </>
                    )}
                    {employee.status && (
                      <>
                        <span className="text-xs text-gray-300">|</span>
                        <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${
                          employee.status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                          employee.status === 'INACTIVE' ? 'bg-gray-100 text-gray-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {employee.status}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className="shrink-0 text-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Users size={16} />
                </div>
              </div>
            ))}
            <div className="p-2 border-t border-emerald-100/60 bg-emerald-50/30">
              <span className="text-xs text-gray-500">
                Press <kbd className="px-1.5 py-0.5 bg-white border border-emerald-200 rounded text-[10px] font-semibold">Enter</kbd> to view all results
              </span>
            </div>
          </div>
        )}

        {/* No Results Message */}
        {showSuggestions && searchQuery.trim().length > 0 && suggestions.length === 0 && (
          <div className="absolute left-0 right-0 top-full mt-2 rounded-xl border border-emerald-200/70 bg-white shadow-2xl shadow-emerald-200/50 z-50 p-6 text-center">
            <div className="flex flex-col items-center gap-2">
              <div className="p-3 bg-gray-100 rounded-full">
                <Search size={20} className="text-gray-400" />
              </div>
              <p className="text-sm font-medium text-gray-700">No employees found</p>
              <p className="text-xs text-gray-500">
                Try adjusting your search terms or{' '}
                <button
                  onClick={() => {
                    navigate(`/employees?search=${encodeURIComponent(searchQuery.trim())}`);
                    setShowSuggestions(false);
                    setSearchQuery("");
                  }}
                  className="text-emerald-600 hover:text-emerald-700 font-medium hover:underline"
                >
                  view all results
                </button>
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="ml-auto flex items-center gap-1">
        {/* <button title="Coming Soon.." className="hidden rounded-full p-2 text-gray-500 hover:bg-emerald-50 hover:text-emerald-700 sm:block transition-colors duration-200">
          <Moon size={18} />
        </button> */}
       
        <Link to="/profile" className="ml-1">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-label-sm font-semibold text-white shadow-md shadow-emerald-500/20 ring-2 ring-emerald-400/30">
            {partner?.name?.[0] ?? "P"}
          </div>
        </Link>
      </div>
    </header>
  );
}